import React, { useState, useEffect } from 'react';
import { Users, Building2, Phone, CreditCard, ArrowLeft, Search, ChevronRight } from 'lucide-react';
import API from '../api';

export default function AdminUserParties() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userParties, setUserParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await API.get('/users/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to fetch users');
    }
    setLoading(false);
  };

  const fetchUserParties = async (userId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await API.get(`/users/admin/user-parties/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserParties(data);
    } catch (error) {
      console.error('Error fetching user parties:', error);
      alert('Failed to fetch user parties');
    }
    setLoading(false);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserParties(user._id);
  };

  const handleBack = () => {
    setSelectedUser(null);
    setUserParties([]);
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.pharmacyName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fmt = (num) =>
    'Rs ' + (Number.isFinite(Number(num)) ? Number(Number(num)).toLocaleString('en-IN') : '0');

  if (loading && !selectedUser) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', fontFamily: "'Poppins', sans-serif" }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {!selectedUser ? (
          <>
            {/* Search Bar */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #f3f4f6',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <div style={{ position: 'relative' }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users by name, email, or pharmacy..."
                  style={{
                    width: '100%',
                    paddingLeft: '48px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Users Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '24px'
            }}>
              {filteredUsers.map((user) => (
                <div
                  key={user._id}
                  onClick={() => handleUserClick(user)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #f3f4f6',
                    padding: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = '#f3f4f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '18px'
                      }}>
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1a202c', fontSize: '16px' }}>
                          {user.name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={20} style={{ color: '#9ca3af', transition: 'color 0.2s' }} />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <Building2 size={16} style={{ color: '#9ca3af' }} />
                      <span style={{ color: '#4b5563' }}>{user.pharmacyName || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <Phone size={16} style={{ color: '#9ca3af' }} />
                      <span style={{ color: '#4b5563' }}>{user.phone || 'N/A'}</span>
                    </div>
                  </div>

                  <div style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid #f3f4f6'
                  }}>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      Member since {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredUsers.length === 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #f3f4f6',
                padding: '60px',
                textAlign: 'center'
              }}>
                <Users size={64} style={{ color: '#d1d5db', margin: '0 auto 16px' }} />
                <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>No users found</p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '20px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <ArrowLeft size={18} />
              Back to Users
            </button>

            {/* User Info Card */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #f3f4f6',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '24px'
                }}>
                  {selectedUser.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1a202c'
                  }}>
                    {selectedUser.name}
                  </h2>
                  <p style={{ margin: '4px 0 0 0', color: '#6b7280' }}>
                    {selectedUser.email}
                  </p>
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px'
                }}>
                  <Building2 size={20} style={{ color: '#3b82f6' }} />
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Pharmacy</div>
                    <div style={{ fontWeight: '600', color: '#1a202c' }}>
                      {selectedUser.pharmacyName || 'N/A'}
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px'
                }}>
                  <Phone size={20} style={{ color: '#3b82f6' }} />
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Phone</div>
                    <div style={{ fontWeight: '600', color: '#1a202c' }}>
                      {selectedUser.phone || 'N/A'}
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px'
                }}>
                  <Users size={20} style={{ color: '#3b82f6' }} />
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Parties</div>
                    <div style={{ fontWeight: '600', color: '#1a202c' }}>
                      {userParties.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Parties Table */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #f3f4f6',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '24px',
                borderBottom: '1px solid #f3f4f6'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1a202c'
                }}>
                  Parties List
                </h3>
              </div>
              
              {loading ? (
                <div style={{
                  padding: '60px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  Loading parties...
                </div>
              ) : userParties.length === 0 ? (
                <div style={{
                  padding: '60px',
                  textAlign: 'center'
                }}>
                  <Building2 size={64} style={{ color: '#d1d5db', margin: '0 auto 16px' }} />
                  <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
                    No parties found for this user
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <tr>
                        <th style={{
                          padding: '16px 24px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Party Name
                        </th>
                        <th style={{
                          padding: '16px 24px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Phone
                        </th>
                        <th style={{
                          padding: '16px 24px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Address
                        </th>
                        <th style={{
                          padding: '16px 24px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          PAN Number
                        </th>
                        <th style={{
                          padding: '16px 24px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Opening Balance
                        </th>
                        <th style={{
                          padding: '16px 24px',
                          textAlign: 'right',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#374151',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userParties.map((party) => (
                        <tr
                          key={party._id}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '600'
                              }}>
                                {party.name?.charAt(0).toUpperCase()}
                              </div>
                              <div style={{ fontWeight: '600', color: '#1a202c' }}>
                                {party.name}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px 24px', color: '#4b5563' }}>
                            {party.phone || '-'}
                          </td>
                          <td style={{ padding: '16px 24px', color: '#4b5563' }}>
                            {party.address || '-'}
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <CreditCard size={16} style={{ color: '#9ca3af' }} />
                              <span style={{ color: '#4b5563' }}>
                                {party.panNumber || '-'}
                              </span>
                            </div>
                          </td>
                          <td style={{
                            padding: '16px 24px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: '#1a202c'
                          }}>
                            {fmt(party.openingBalance || 0)}
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontWeight: '600',
                              fontSize: '14px',
                              backgroundColor: (party.balance || 0) > 0 
                                ? '#d1fae5' 
                                : (party.balance || 0) < 0 
                                ? '#fee2e2' 
                                : '#f3f4f6',
                              color: (party.balance || 0) > 0 
                                ? '#065f46' 
                                : (party.balance || 0) < 0 
                                ? '#991b1b' 
                                : '#374151'
                            }}>
                              {fmt(party.balance || 0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}